/**
 * THE SPENDING-LIMIT HOLD AS THE CHAT WINDOW SEES IT (2026-10-08, the dev
 * browser pass of that day: "a typing indicator that vanished, then nothing;
 * only a notification and toast said why").
 *
 * When the payer's daily spending limit holds an agent turn, the router
 * (lib/agents/router.ts, the daily-cap branch) says so with ONE streaming
 * delta before the turn starts: a `tool_progress` of kind `system`, marked
 * `notice: 'spending_hold'`, on the turn's response id, with the agent and
 * NO message id, because the placeholder row is created only after the
 * pre-flight checks pass. That last fact is why the chat view cannot show it
 * the way it shows every other delta: overlays render inside the message
 * whose `uampEventId` is the response id, and at hold time there is no such
 * message. So the hold is kept apart from the stream buffers, as one held
 * turn, and drawn as one plain line at the foot of the conversation until
 * the turn resumes (any later delta of that response that is not the hold
 * itself) or ends (done, error, cancelled).
 *
 * Pure, so the decision is testable without the component: the view wires
 * these two predicates into its `response.delta` handler and its end
 * handlers (tests/unit/components/chat-spending-hold.test.ts pins both the
 * predicates and the wiring).
 */

/** The router's marker on the hold notice. One string, shared by name with lib/agents/router.ts. */
export const SPENDING_HOLD_NOTICE = 'spending_hold';

/**
 * The payment skill's marker on a tool call held for the person's spending
 * approval, mid-turn (a first charge from an agent, the consent floor, a
 * top-up). Unlike the hold above, the turn's message and the tool's card
 * already exist, so it rides on the card (`call_id`) and the card says it is
 * waiting. One string, shared by name with lib/agents/portal-payment-skill.ts.
 */
export const APPROVAL_WAIT_NOTICE = 'approval_wait';

export interface HeldTurn {
  responseId: string;
  agentId: string;
}

interface DeltaFrame {
  response_id?: string;
  delta?: { type?: string; text?: string; tool_progress?: { kind?: unknown; notice?: unknown; text?: unknown } | null } | null;
  _portal?: { chatId?: string; messageId?: string; agentId?: string } | null;
}

/**
 * The hold, when this frame is it: a system progress notice carrying the
 * marker, on a response id, with no message yet. A frame with a message id is
 * an ordinary overlay delta even if it carried the marker, so a server that
 * one day says the hold inside a placeholder does not draw it twice.
 */
export function heldTurnFromDelta(data: DeltaFrame): HeldTurn | null {
  const rid = typeof data.response_id === 'string' ? data.response_id : '';
  if (!rid) return null;
  const tp = data.delta?.type === 'tool_progress' ? data.delta.tool_progress : null;
  if (!tp || tp.kind !== 'system' || tp.notice !== SPENDING_HOLD_NOTICE) return null;
  if (data._portal?.messageId) return null;
  return { responseId: rid, agentId: typeof data._portal?.agentId === 'string' ? data._portal.agentId : '' };
}

/**
 * Whether this frame means the held turn is running again: any delta of the
 * SAME response that is not itself the hold. A delta of another response
 * (another agent in the room, a straggler) leaves the hold standing.
 */
export function deltaResumesHeldTurn(held: HeldTurn | null, data: DeltaFrame): boolean {
  if (!held) return false;
  if (data.response_id !== held.responseId) return false;
  return heldTurnFromDelta(data) === null;
}
